import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../utils/api';
import { CreateRole, RoleList } from '../dtos/role';
import { accessToken } from '../utils/helper';
import { ApiResponseError } from '../dtos/common';

type RoleListResponse = { data: RoleList[]; total: number; totalPages: number };
type RoleMessageResponse = { message: string };

const getApiErrorMessage = (error: unknown) => {
  const apiError = error as ApiResponseError;

  return apiError.response?.data?.message || 'Something Went Wrong!';
};

interface RoleState {
  roleList: RoleListResponse;
  loading: boolean;
  error: string | null;
  addRoleResponse: RoleMessageResponse;
  addRoleError: string | null;
  updateRoleResponse: RoleMessageResponse;
  updateRoleError: string | null;
  deleteRoleResponse: RoleMessageResponse;
  deleteRoleError: string | null;
}

const initialState: RoleState = {
  roleList: { data: [], total: 0, totalPages: 0 },
  addRoleResponse: { message: '' },
  updateRoleResponse: { message: '' },
  deleteRoleResponse: { message: '' },
  loading: false,
  error: null,
  addRoleError: null,
  updateRoleError: null,
  deleteRoleError: null,
};

export const getAllRolesTrigger = createAsyncThunk(
  'get/roles',
  async (
    {
      page,
      limit,
      search,
      sortOrder,
      sortField,
    }: {
      page?: number;
      limit?: number;
      search?: string;
      sortOrder?: string;
      sortField?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const queryParams = new URLSearchParams();
      if (page) queryParams.append('page', page.toString());
      if (limit) queryParams.append('limit', limit.toString());
      if (search) queryParams.append('search', search);
      if (sortOrder) queryParams.append('sortOrder', sortOrder);
      if (sortField) queryParams.append('sortField', sortField);

      const response = await api.get(`/roles?${queryParams}`, accessToken());
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const addRoleTrigger = createAsyncThunk(
  '/roles/add',
  async (request: CreateRole, { rejectWithValue }) => {
    try {
      const response = await api.post<RoleMessageResponse>(
        `/roles/add`,
        request,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const updateRoleTrigger = createAsyncThunk(
  '/roles/update/:id',
  async ({ id, request }: { id: number; request: CreateRole }, { rejectWithValue }) => {
    try {
      const response = await api.patch<RoleMessageResponse>(
        `/roles/update/${id}`,
        request,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

export const deleteRoleTrigger = createAsyncThunk(
  '/roles/delete/:id',
  async (id: number | undefined, { rejectWithValue }) => {
    try {
      const response = await api.delete<RoleMessageResponse>(
        `/roles/delete/${id}`,
        accessToken(),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error));
    }
  },
);

// Create slice
const roleSlice = createSlice({
  name: 'role',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAllRolesTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllRolesTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.roleList = action.payload;
      })
      .addCase(getAllRolesTrigger.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to load roles';
      })
      .addCase(addRoleTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(addRoleTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.addRoleResponse = action.payload;
      })
      .addCase(addRoleTrigger.rejected, (state, action) => {
        state.loading = false;
        state.addRoleError =
          (action.payload as string) || action.error.message || 'Failed to save role';
      })
      .addCase(updateRoleTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateRoleTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.updateRoleResponse = action.payload;
      })
      .addCase(updateRoleTrigger.rejected, (state, action) => {
        state.loading = false;
        state.updateRoleError =
          (action.payload as string) || action.error.message || 'Failed to update role';
      })
      .addCase(deleteRoleTrigger.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteRoleTrigger.fulfilled, (state, action) => {
        state.loading = false;
        state.deleteRoleResponse = action.payload;
      })
      .addCase(deleteRoleTrigger.rejected, (state, action) => {
        state.loading = false;
        state.deleteRoleError =
          (action.payload as string) || action.error.message || 'Failed to delete role';
      });
  },
});

// Export the reducer to be added to the store
export default roleSlice.reducer;
